import type { ID } from '@octopus-do/octopus-project-core'
import { buffers, type Channel } from 'redux-saga'
import { actionChannel, call, put, select, take } from 'redux-saga/effects'

import { fetchNest } from '@/nest'
import { ErrorNotification } from '@/store_old/reducers/notification'
import { InternalDashboardActions } from '../actions'
import { selectProjectParents } from '../selectors'
import {
  StatusActions,
  type StatusOperation,
  type StatusResult,
} from '../statuses'
import type { ProjectParents } from '../types'
import { parseNestError } from '../utils'
import { dashboardInit } from './dashboardInit'

export const requestStatus = (projectId: ID, operation: StatusOperation) => {
  const path = `/projects/${encodeURIComponent(projectId)}/statuses`
  switch (operation.type) {
    case 'load':
      return fetchNest<StatusResult>(path)
    case 'set':
      return fetchNest<StatusResult>(`${path}/selected`, {
        method: 'PATCH',
        body: JSON.stringify({ statusId: operation.statusId }),
      })
    case 'create':
      return fetchNest<StatusResult>(path, {
        method: 'POST',
        body: JSON.stringify({
          title: operation.title,
          color: operation.color,
        }),
      })
    case 'update':
      return fetchNest<StatusResult>(
        `${path}/${encodeURIComponent(operation.id)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(operation.changes),
        }
      )
    case 'delete':
      return fetchNest<StatusResult>(
        `${path}/${encodeURIComponent(operation.id)}`,
        { method: 'DELETE' }
      )
    case 'move':
      return fetchNest<StatusResult>(
        `${path}/${encodeURIComponent(operation.id)}/order`,
        {
          method: 'PATCH',
          body: JSON.stringify({ direction: operation.direction }),
        }
      )
  }
}

// Direct editor links have no dashboard data yet. Load the same workspace
// records before reusing the status library and permission-aware menu.
export function* resolveStatusParents(projectId: ID) {
  const parents: ProjectParents | null = yield select(
    selectProjectParents(projectId)
  )
  if (parents) {
    return parents
  }
  yield call(dashboardInit)
  const loaded: ProjectParents | null = yield select(
    selectProjectParents(projectId)
  )
  return loaded
}

// A response always includes the complete library. Serialize requests so an
// older load cannot overwrite a later rename, assignment or deletion.
export function* statusesSaga() {
  const channel: Channel<ReturnType<typeof StatusActions.request>> =
    yield actionChannel(StatusActions.request, buffers.expanding())
  while (true) {
    const {
      payload: { projectId, operation },
    }: ReturnType<typeof StatusActions.request> = yield take(channel)
    const parents: ProjectParents | null = yield call(
      resolveStatusParents,
      projectId
    )
    if (!parents) {
      continue
    }
    const workspaceId = parents.workspace.id
    yield put(
      InternalDashboardActions.update({
        workspaceId,
        data: { statusesBusy: true, statusesError: false },
      })
    )
    try {
      const result: StatusResult = yield call(
        requestStatus,
        projectId,
        operation
      )
      yield put(StatusActions.received({ projectId, workspaceId, ...result }))
    } catch (error) {
      yield put(
        InternalDashboardActions.update({
          workspaceId,
          data: { statusesError: true },
        })
      )
      yield put(ErrorNotification(parseNestError(error)))
    } finally {
      yield put(
        InternalDashboardActions.update({
          workspaceId,
          data: { statusesBusy: false },
        })
      )
    }
  }
}
